// /team/people: groups, extra roles, and the member privacy page.
// Mounted inside TeamPanel so it shares the shell, theme, and /teams/me.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Theme, themeColors } from '../theme/colors';
import {
  TeamMe,
  TeamMemberRow,
  ExtraRoleKey,
  TeamGroupsPayload,
  TeamRolesPayload,
  TeamError,
  TeamUnauthorizedError,
  getTeamGroups,
  createTeamGroup,
  archiveTeamGroup,
  setTeamGroupMembers,
  getTeamRoles,
  assignTeamRole,
  revokeTeamRole,
} from './chat/teamService';
import TeamGuide from './TeamGuide';

export interface TeamPeopleProps {
  theme: Theme;
  me: TeamMe;
}

const TeamPeople: React.FC<TeamPeopleProps> = ({ theme, me }) => {
  const c = themeColors[theme];
  const members = me.members ?? [];
  const myId = me.user_id ?? null;

  const [groups, setGroups] = useState<TeamGroupsPayload | null>(null);
  const [roles, setRoles] = useState<TeamRolesPayload | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [roleUserId, setRoleUserId] = useState<number | ''>('');
  const [roleKey, setRoleKey] = useState<ExtraRoleKey>('admin');
  const [roleGroupId, setRoleGroupId] = useState<number | ''>('');
  const [confirmArchiveId, setConfirmArchiveId] = useState<number | null>(null);

  const cardStyle: React.CSSProperties = {
    background: c.panel,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '18px',
  };
  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: c.ink3,
    margin: '26px 2px 10px',
  };
  const inputStyle: React.CSSProperties = {
    background: c.panel2,
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '13.5px',
    color: c.ink,
    outline: 'none',
    fontFamily: 'inherit',
    minWidth: 0,
  };
  const ghostBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '13px',
    fontWeight: 500,
    color: c.ink,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: '44px',
  };
  const primaryBtnStyle: React.CSSProperties = {
    background: c.btnPrimaryBg,
    color: c.btnPrimaryText,
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: '44px',
    ['--pa-btn-hover' as any]: c.btnPrimaryHover,
  };
  const dangerColor = theme === 'light' ? '#DC2626' : '#F87171';

  const emailById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const row of members) map[row.user_id] = row.email;
    return map;
  }, [members]);

  const describe = (err: unknown) => {
    if (err instanceof TeamUnauthorizedError) {
      window.location.href = '/login';
      return '';
    }
    if (err instanceof TeamError) return err.detail;
    return 'Gagal memuat. Coba lagi.';
  };

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const [g, r] = await Promise.all([getTeamGroups(), getTeamRoles()]);
      setGroups(g);
      setRoles(r);
      setStatus('ready');
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
      setStatus('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<void>, okNotice: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      await load();
      setNotice(okNotice);
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleCreateGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    run(async () => {
      await createTeamGroup(name);
      setNewGroupName('');
    }, 'Grup dibuat.');
  };

  const toggleMember = (groupId: number, userId: number, selected: boolean) => {
    const group = groups?.groups.find((g) => g.id === groupId);
    if (!group) return;
    const next = selected
      ? [...group.member_ids, userId]
      : group.member_ids.filter((id) => id !== userId);
    run(async () => {
      await setTeamGroupMembers(groupId, next);
    }, 'Anggota grup diperbarui.');
  };

  const handleAssignRole = () => {
    if (roleUserId === '') return;
    if (roleKey === 'manager' && roleGroupId === '') return;
    run(async () => {
      await assignTeamRole(
        Number(roleUserId),
        roleKey,
        roleKey === 'manager' ? Number(roleGroupId) : null,
      );
      setRoleUserId('');
      setRoleGroupId('');
    }, 'Role ditetapkan.');
  };

  if (status === 'loading' && !groups) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: '13.5px', color: c.ink2 }}>Memuat grup…</div>
      </div>
    );
  }

  if (status === 'error' && !groups) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: '13.5px', color: c.ink, marginBottom: '10px' }}>
          Gagal memuat grup.
        </div>
        <div style={{ fontSize: '12.5px', color: c.ink2, marginBottom: '14px' }}>{error}</div>
        <button type="button" onClick={load} className="pa-ghost" style={ghostBtnStyle}>
          Coba lagi
        </button>
      </div>
    );
  }

  const canManage = !!groups?.can_manage_groups;
  const canAssign = !!roles?.can_assign_roles;
  const privacy = roles?.privacy;
  const myGroup = groups?.groups.find((g) => myId != null && g.member_ids.includes(myId));

  return (
    <>
      <div style={sectionLabelStyle}>Grup</div>
      <div style={cardStyle}>
        <TeamGuide
          theme={theme}
          lead="Satu orang satu grup menurut fungsi atau lokasi, misalnya Sales. Playbook dan Program bisa diarahkan ke grup itu. Playbook tetap jalan meski belum ada grup."
          exampleName="Sales, CS, Finance"
          exampleBody="Sari masuk Sales. Manager Sales melihat progres grupnya. Champion AI di CS menulis draft Playbook untuk CS. Tagihan tetap ke pemilik tim."
          steps={
            canManage
              ? [
                  'Buat grup, misalnya Sales.',
                  'Centang anggotanya. Satu orang hanya masuk satu grup.',
                  'Kalau perlu, tetapkan Manager atau Champion AI di bawah.',
                ]
              : [
                  'Lihat grup kamu di sini. Admin yang menambah atau memindahkan anggota.',
                  'Playbook dan Program yang diarahkan ke grup ini akan muncul untuk kamu.',
                ]
          }
        />
        {myGroup && (
          <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '14px' }}>
            Grup kamu: <strong style={{ color: c.ink, fontWeight: 600 }}>{myGroup.name}</strong>
          </div>
        )}
        {canManage && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '12px 0 4px' }}>
            <input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Nama grup, misalnya Sales"
              maxLength={60}
              className="pa-field"
              style={{ ...inputStyle, flex: '1 1 180px' }}
            />
            <button
              type="button"
              onClick={handleCreateGroup}
              disabled={busy || !newGroupName.trim()}
              className="pa-primary"
              style={{ ...primaryBtnStyle, opacity: busy || !newGroupName.trim() ? 0.45 : 1 }}
            >
              Buat grup
            </button>
          </div>
        )}
      </div>

      {(groups?.groups ?? []).map((group) => (
        <div key={group.id} style={{ ...cardStyle, marginTop: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>{group.name}</div>
              {group.description && (
                <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '4px' }}>{group.description}</div>
              )}
              <div style={{ fontSize: '12px', color: c.ink3, marginTop: '6px' }}>
                {group.member_ids.length} anggota
                {group.manager_ids.length ? ` · ${group.manager_ids.length} manager` : ''}
              </div>
            </div>
            {canManage && (
              confirmArchiveId === group.id ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="pa-ghost"
                    style={{ ...ghostBtnStyle, color: dangerColor, borderColor: dangerColor }}
                    disabled={busy}
                    onClick={() => {
                      setConfirmArchiveId(null);
                      run(async () => { await archiveTeamGroup(group.id); }, 'Grup diarsipkan.');
                    }}
                  >
                    Yakin arsipkan?
                  </button>
                  <button
                    type="button"
                    className="pa-ghost"
                    style={ghostBtnStyle}
                    onClick={() => setConfirmArchiveId(null)}
                  >
                    Batal
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="pa-ghost"
                  style={ghostBtnStyle}
                  onClick={() => setConfirmArchiveId(group.id)}
                >
                  Arsipkan
                </button>
              )
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '14px' }}>
            {members.map((row: TeamMemberRow) => {
              const on = group.member_ids.includes(row.user_id);
              const clickable = group.can_edit_members && !busy;
              return (
                <button
                  key={row.user_id}
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && toggleMember(group.id, row.user_id, !on)}
                  className="pa-ghost"
                  style={{
                    ...ghostBtnStyle,
                    minHeight: '44px',
                    background: on ? c.jadeDim : 'transparent',
                    borderColor: on ? c.jade : c.line2,
                    color: on ? c.ink : c.ink2,
                    opacity: clickable ? 1 : 0.7,
                    cursor: clickable ? 'pointer' : 'default',
                  }}
                >
                  {row.email}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {groups && groups.groups.length === 0 && (
        <div style={{ ...cardStyle, marginTop: '10px', fontSize: '13px', color: c.ink2 }}>
          Belum ada grup. {canManage ? 'Buat yang pertama di atas.' : 'Admin tim yang membuatnya.'}
        </div>
      )}

      {groups && groups.ungrouped.length > 0 && (
        <>
          <div style={sectionLabelStyle}>Belum dikelompokkan</div>
          <div style={{ ...cardStyle, fontSize: '13px', color: c.ink2, lineHeight: 1.7 }}>
            {groups.ungrouped.map((row) => row.email).join(', ')}
          </div>
        </>
      )}

      {canAssign && roles && (
        <>
          <div style={sectionLabelStyle}>Role tambahan</div>
          <div style={cardStyle}>
            <div style={{ fontSize: '12.5px', color: c.ink2, lineHeight: 1.65, marginBottom: '12px' }}>
              Admin, Manager, dan Champion AI di atas role anggota. Tagihan tetap ke pemilik tim.
              Contoh: tetapkan Sari sebagai Champion AI supaya dia bisa menulis draft Playbook.
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <select
                value={roleUserId}
                onChange={(e) => setRoleUserId(e.target.value ? Number(e.target.value) : '')}
                className="pa-field"
                style={{ ...inputStyle, minHeight: '44px', flex: '1 1 160px' }}
              >
                <option value="">Pilih anggota</option>
                {members.filter((m) => m.role !== 'owner').map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.email}</option>
                ))}
              </select>
              <select
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value as ExtraRoleKey)}
                className="pa-field"
                style={{ ...inputStyle, minHeight: '44px', flex: '1 1 140px' }}
              >
                {roles.catalog.map((item) => (
                  <option key={item.key} value={item.key}>{item.label}</option>
                ))}
              </select>
              {roleKey === 'manager' && (
                <select
                  value={roleGroupId}
                  onChange={(e) => setRoleGroupId(e.target.value ? Number(e.target.value) : '')}
                  className="pa-field"
                  style={{ ...inputStyle, minHeight: '44px', flex: '1 1 140px' }}
                >
                  <option value="">Pilih grup</option>
                  {(groups?.groups ?? []).map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={handleAssignRole}
                disabled={busy || roleUserId === '' || (roleKey === 'manager' && roleGroupId === '')}
                className="pa-primary"
                style={{
                  ...primaryBtnStyle,
                  opacity: busy || roleUserId === '' || (roleKey === 'manager' && roleGroupId === '') ? 0.45 : 1,
                }}
              >
                Tetapkan
              </button>
            </div>
            {roles.catalog.map((item) => (
              <div key={item.key} style={{ fontSize: '12px', color: c.ink3, marginBottom: '6px', lineHeight: 1.5 }}>
                <strong style={{ color: c.ink2, fontWeight: 600 }}>{item.label}.</strong> {item.description}
              </div>
            ))}
            {roles.assignments.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                {roles.assignments.map((row) => {
                  const label = roles.catalog.find((item) => item.key === row.role_key)?.label || row.role_key;
                  const groupName = groups?.groups.find((g) => g.id === row.scope_group_id)?.name;
                  return (
                    <div
                      key={row.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '10px',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderTop: `1px solid ${c.line}`,
                        minHeight: '44px',
                      }}
                    >
                      <div style={{ fontSize: '13px', color: c.ink }}>
                        {emailById[row.user_id] || `Anggota ${row.user_id}`}
                        <span style={{ color: c.ink3 }}> · {label}{groupName ? ` · ${groupName}` : ''}</span>
                      </div>
                      <button
                        type="button"
                        className="pa-ghost"
                        style={ghostBtnStyle}
                        disabled={busy}
                        onClick={() => run(async () => { await revokeTeamRole(row.id); }, 'Role dicabut.')}
                      >
                        Cabut
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {privacy && (
        <>
          <div style={sectionLabelStyle}>{privacy.heading}</div>
          <div style={cardStyle}>
            <div style={{ fontSize: '13.5px', color: c.ink, lineHeight: 1.65, marginBottom: '14px' }}>
              {privacy.intro}
            </div>
            {privacy.seen.map((row) => (
              <div key={row.what} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: c.ink }}>{row.what}</div>
                <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '2px', lineHeight: 1.55 }}>{row.why}</div>
              </div>
            ))}
            <div style={{ fontSize: '12px', fontWeight: 600, color: c.ink3, margin: '16px 0 8px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Admin tidak melihat
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: c.ink, lineHeight: 1.7 }}>
              {privacy.never.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </>
      )}

      {(error || notice) && (
        <div style={{ marginTop: '12px', fontSize: '12.5px', color: error ? dangerColor : c.ink2 }}>
          {error || notice}
        </div>
      )}
    </>
  );
};

export default TeamPeople;
